import React, { useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useIntl } from 'react-intl';
import { ExcelCellData } from '../../types/excelView';

interface ExcelCellProps {
  cell: ExcelCellData | null;
  isPr: boolean;
  isDeload: boolean;
  isSaving: boolean;
  onUpdate: (exerciseInstanceId: number, patch: { repetitions?: string; weight?: string }) => Promise<void> | void;
}

/**
 * One cell of the Excel view. Renders `reps × weight` in monospace and a small
 * indicator dot (★ PR / ↓ deload). Click → popover with quick reps + weight
 * editors (commit on blur).
 *
 * Empty cells (no session that week) render `—` and don't open the popover.
 */
const ExcelCell: React.FC<ExcelCellProps> = ({ cell, isPr, isDeload, isSaving, onUpdate }) => {
  const intl = useIntl();
  const overlayRef = useRef<OverlayPanel>(null);
  const [reps, setReps] = useState(cell?.repetitions ?? '');
  const [weight, setWeight] = useState(cell?.weight ?? '');

  // Sync local state when cell changes externally (refresh after bulk action).
  React.useEffect(() => {
    setReps(cell?.repetitions ?? '');
    setWeight(cell?.weight ?? '');
  }, [cell?.repetitions, cell?.weight]);

  if (!cell) {
    return (
      <td
        className="et-excel__cell et-excel__cell--empty"
        title={intl.formatMessage({
          id: 'excelView.cell.empty',
          defaultMessage: 'Sin sesión planificada esta semana'
        })}
      >
        —
      </td>
    );
  }

  const handleOpen = (e: React.MouseEvent<HTMLTableCellElement>) => {
    overlayRef.current?.toggle(e);
  };

  const flush = async () => {
    const repsChanged = reps !== (cell.repetitions ?? '');
    const weightChanged = weight !== (cell.weight ?? '');
    if (!repsChanged && !weightChanged) return;
    // Only include keys that actually changed — passing `undefined` would
    // get spread into the optimistic update and clear the other field locally
    // until the refetch lands.
    const patch: { repetitions?: string; weight?: string } = {};
    if (repsChanged) patch.repetitions = reps;
    if (weightChanged) patch.weight = weight;
    await onUpdate(cell.exerciseInstanceId, patch);
  };

  const display = `${cell.repetitions ?? '—'} × ${cell.weight ?? '—'}kg`;
  const className = ['et-excel__cell', isSaving ? 'is-saving' : '', isPr ? 'is-pr' : '', isDeload ? 'is-deload' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <td className={className} onClick={handleOpen}>
      <span className="et-excel__cell-value">{display}</span>
      {isPr && (
        <span className="et-excel__indicator et-excel__indicator--pr" aria-label="PR">
          ★
        </span>
      )}
      {isDeload && (
        <span className="et-excel__indicator et-excel__indicator--deload" aria-label="Deload">
          ↓
        </span>
      )}
      <OverlayPanel ref={overlayRef} dismissable className="et-excel__popover">
        <div className="flex flex-column gap-2" style={{ minWidth: 200 }}>
          <label className="text-sm font-semibold">
            {intl.formatMessage({ id: 'session.reps', defaultMessage: 'Reps' })}
          </label>
          <InputText
            value={reps}
            onChange={(e) => setReps(e.target.value)}
            onBlur={flush}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                flush();
                overlayRef.current?.hide();
              }
            }}
            className="p-inputtext-sm"
            onClick={(e) => e.stopPropagation()}
          />
          <label className="text-sm font-semibold mt-1">
            {intl.formatMessage({ id: 'session.weight', defaultMessage: 'Peso (kg)' })}
          </label>
          <InputText
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            onBlur={flush}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                flush();
                overlayRef.current?.hide();
              }
            }}
            className="p-inputtext-sm"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="text-xs" style={{ color: 'var(--ios-text-tertiary)' }}>
            {intl.formatMessage({ id: 'excelView.cell.sets', defaultMessage: '{n} sets' }, { n: cell.sets ?? '?' })}
          </div>
          <Button
            label={intl.formatMessage({ id: 'common.close', defaultMessage: 'Cerrar' })}
            icon="pi pi-times"
            className="p-button-text p-button-sm align-self-end"
            onClick={(e) => {
              e.stopPropagation();
              overlayRef.current?.hide();
            }}
          />
        </div>
      </OverlayPanel>
    </td>
  );
};

export default ExcelCell;
